import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Fab,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { useAuth } from '../contexts/AuthContext';
import { listBots, deleteBot, runBot } from '../services/bots';
import type { Bot } from '../types';
import BotDialog from '../components/bots/BotDialog';
import BotRunHistory from '../components/bots/BotRunHistory';

const TRIGGER_LABELS: Record<string, string> = {
  manual: 'Manual',
  on_item_add: 'On Item Add',
  schedule: 'Scheduled',
};

const TRIGGER_COLORS: Record<string, 'default' | 'primary' | 'secondary'> = {
  manual: 'default',
  on_item_add: 'primary',
  schedule: 'secondary',
};

export default function BotsPage() {
  const { user } = useAuth();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editBot, setEditBot] = useState<Bot | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Bot | null>(null);
  const [historyBot, setHistoryBot] = useState<Bot | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuBot, setMenuBot] = useState<Bot | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      setBots(await listBots());
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, bot: Bot) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuBot(bot);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuBot(null);
  };

  const handleEdit = () => {
    setEditBot(menuBot);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleRunNow = async () => {
    const bot = menuBot;
    handleMenuClose();
    if (!bot) return;

    setRunningId(bot.id);
    try {
      const result = await runBot(bot.id);
      const preview = result.result?.slice(0, 200) || 'Completed';
      setSnackbar({ message: `${bot.name}: ${preview}`, severity: result.status === 'completed' ? 'success' : 'error' });
      load();
    } catch {
      setSnackbar({ message: `${bot.name}: Failed to run`, severity: 'error' });
    } finally {
      setRunningId(null);
    }
  };

  const handleViewRuns = () => {
    setHistoryBot(menuBot);
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteTarget(menuBot);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBot(deleteTarget.id);
      load();
    } catch {
      setSnackbar({ message: 'Failed to delete bot', severity: 'error' });
    }
    setDeleteTarget(null);
  };

  const handleCreate = () => {
    setEditBot(null);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Bots
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : bots.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          No bots yet. Create one to automate tasks with your saved items.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {bots.map((bot) => (
            <Grid key={bot.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardActionArea onClick={() => { setEditBot(bot); setDialogOpen(true); }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PrecisionManufacturingIcon color="action" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {bot.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, bot)}
                      >
                        {runningId === bot.id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <MoreVertIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={TRIGGER_LABELS[bot.triggerType] ?? bot.triggerType}
                        size="small"
                        color={TRIGGER_COLORS[bot.triggerType] ?? 'default'}
                        variant="outlined"
                      />
                      {bot.triggerType === 'schedule' && bot.schedule && (
                        <Chip label={bot.schedule} size="small" variant="outlined" />
                      )}
                    </Box>
                    {bot.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {bot.description}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {bot.instructions}
                    </Typography>
                    {bot.lastRunAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Last run: {new Date(bot.lastRunAt).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleRunNow}>Run Now</MenuItem>
        <MenuItem onClick={handleViewRuns}>View Runs</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      <Fab
        color="primary"
        onClick={handleCreate}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>

      <BotDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditBot(null); }}
        onSaved={load}
        bot={editBot}
      />

      <BotRunHistory
        open={!!historyBot}
        onClose={() => setHistoryBot(null)}
        botId={historyBot?.id ?? null}
        botName={historyBot?.name ?? ''}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Bot</DialogTitle>
        <DialogContent>
          <Typography>
            Delete "{deleteTarget?.name}"? All run history will also be removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
            sx={{ maxWidth: 500 }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
